import { useState, useEffect, useCallback, useRef, type SetStateAction } from 'react'
import { TranscriptItem, LiveConnectionState } from 'types'
import { clientSessionViewStore } from '../../../services/clientSessionViewStore.js'
import { logger } from '../../lib/logger-client.js'
import {
  clientSessionIdWasRestored,
  DEAD_SESSION_LOOKUP_MS,
  ensureSessionCapability,
  replaceDeadClientSession,
  SESSION_HEADER,
  trackClientSessionLookup,
} from '../../core/session/session-identity.js'
import { acceptSessionProjectionEnvelope } from '../../lib/accept-session-projection-response.js'

/**
 * DECISION FOR FARZAD: network-failure guard only.
 * A missing id is a completed GET /api/session 404 (api/session.ts). This timer
 * covers a fetch that never completes: the browser never receives that 404.
 * It does not decide that a slow but answering lookup is a dead session by itself
 * when the server has already responded.
 */

function withTimeout<T>(work: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('session-lookup-timeout')), ms)
    work.then(
      value => {
        clearTimeout(timer)
        resolve(value)
      },
      error => {
        clearTimeout(timer)
        reject(error)
      }
    )
  })
}

interface UseChatSessionProps {
  connectionState: LiveConnectionState
  sessionId: string
}

interface BackendStatus {
  mode: 'idle' | 'agents' | 'fallback' | 'voice'
  message: string
  severity: 'info' | 'warn' | 'error'
}

interface ApplyServerTurnResultParams {
  transcript: TranscriptItem[]
  backendStatus?: BackendStatus
  projectionEnvelope?: unknown
}

export function useChatSession({ connectionState, sessionId }: UseChatSessionProps) {
  const [transcript, setTranscriptState] = useState<TranscriptItem[]>(
    () => clientSessionViewStore.getSnapshot().transcript
  )

  // Internal ref for transcript to avoid stale closures if needed
  const transcriptRef = useRef<TranscriptItem[]>(transcript)

  const [backendStatus, setBackendStatus] = useState<BackendStatus>({
    mode: 'idle',
    message: 'Agents available - ready for input',
    severity: 'info',
  })

  // React transcript is a derived view of clientSessionViewStore.transcript.
  useEffect(() => {
    const sync = (state: ReturnType<typeof clientSessionViewStore.getSnapshot>) => {
      transcriptRef.current = state.transcript
      setTranscriptState(state.transcript)
    }
    sync(clientSessionViewStore.getSnapshot())
    const unsubscribe = clientSessionViewStore.subscribe(sync)
    return () => {
      unsubscribe()
    }
  }, [])

  const setTranscript = useCallback((next: SetStateAction<TranscriptItem[]>) => {
    clientSessionViewStore.updateOptimisticTranscript(current =>
      typeof next === 'function'
        ? (next as (prev: TranscriptItem[]) => TranscriptItem[])(current)
        : next
    )
  }, [])

  // Update backend status based on connection state
  useEffect(() => {
    if (connectionState === LiveConnectionState.CONNECTED) {
      setBackendStatus({
        mode: 'voice',
        message: 'Voice: Gemini Live connected',
        severity: 'info',
      })
    } else if (connectionState === LiveConnectionState.ERROR) {
      setBackendStatus({
        mode: 'fallback',
        message: 'Voice connection error - text will use chat backend',
        severity: 'warn',
      })
    } else if (connectionState === LiveConnectionState.DISCONNECTED) {
      setBackendStatus(prev =>
        prev.mode === 'voice'
          ? {
              mode: 'idle',
              message: 'Voice disconnected - text chat ready',
              severity: 'info',
            }
          : prev
      )
    }
  }, [connectionState])

  const hydrateSessionProjection = useCallback(
    async (
      targetSessionId = sessionId,
      signal?: AbortSignal,
      options?: { force?: boolean },
    ): Promise<boolean> => {
      if (!targetSessionId.trim()) return false
      const restored = clientSessionIdWasRestored(targetSessionId)
      const expectsCommittedSession =
        options?.force === true ||
        restored ||
        clientSessionViewStore.getServerProjectionSnapshot() !== null ||
        (typeof sessionStorage !== 'undefined' &&
          sessionStorage.getItem('fbc_briefing_confirmed') === '1')
      if (!expectsCommittedSession) return false

      const retireDeadStoredSession = () => {
        if (!clientSessionIdWasRestored(targetSessionId)) return
        // The mount GET has no timeout, so a 404 can arrive after the 4s cap.
        // Skip when this id is no longer the open session, or when consent
        // already landed a projection for it. A late 404 must not mint a new id.
        const currentId = clientSessionViewStore.getSnapshot().sessionId?.trim()
        if (currentId !== targetSessionId) return
        const projection = clientSessionViewStore.getServerProjectionSnapshot()
        if (projection?.sessionId === targetSessionId) return
        const next = replaceDeadClientSession(targetSessionId)
        clientSessionViewStore.adoptSessionId(next)
        logger.warn('[useChatSession] Replaced a dead stored session', {
          sessionId: targetSessionId,
        })
      }

      try {
        const capability = ensureSessionCapability(targetSessionId)
        await (options?.force ? withTimeout(capability, DEAD_SESSION_LOOKUP_MS) : capability)
        const lookup = new AbortController()
        const timer = options?.force
          ? setTimeout(() => lookup.abort(), DEAD_SESSION_LOOKUP_MS)
          : null
        const onCallerAbort = () => lookup.abort()
        signal?.addEventListener('abort', onCallerAbort)
        let response: Response
        try {
          response = await fetch('/api/session', {
            method: 'GET',
            credentials: 'same-origin',
            headers: { [SESSION_HEADER]: targetSessionId },
            signal: lookup.signal,
          })
        } finally {
          if (timer) clearTimeout(timer)
          signal?.removeEventListener('abort', onCallerAbort)
        }

        // A new browser session has no committed projection yet.
        // A restored cookie that 404s is a dead id and must not be reused.
        if (response.status === 404) {
          await retireDeadStoredSession()
          return false
        }

        if (!response.ok) {
          logger.warn('[useChatSession] Canonical session hydration failed', {
            sessionId: targetSessionId,
            status: response.status,
          })
          return false
        }

        const payload: unknown = await response.json()
        return acceptSessionProjectionEnvelope(payload, 'GET /api/session')
      } catch (error) {
        const timedOut = error instanceof Error && error.message === 'session-lookup-timeout'
        const aborted = error instanceof DOMException && error.name === 'AbortError'
        if (aborted && signal?.aborted) return false
        if (options?.force && (timedOut || aborted)) {
          await retireDeadStoredSession()
          return false
        }
        logger.warn('[useChatSession] Canonical session hydration failed', {
          sessionId: targetSessionId,
          error: error instanceof Error ? error.message : String(error),
        })
        return false
      }
    },
    [sessionId]
  )

  useEffect(() => {
    const controller = new AbortController()
    trackClientSessionLookup(hydrateSessionProjection(sessionId, controller.signal))
    return () => {
      controller.abort()
    }
  }, [hydrateSessionProjection, sessionId])

  const applyServerTurnResult = useCallback((params: ApplyServerTurnResultParams) => {
    if (params.backendStatus) {
      setBackendStatus(params.backendStatus)
    }

    if (params.projectionEnvelope !== undefined) {
      const accepted = acceptSessionProjectionEnvelope(
        params.projectionEnvelope,
        'chat turn response'
      )
      if (accepted || clientSessionViewStore.getServerProjectionSnapshot() !== null) {
        transcriptRef.current = clientSessionViewStore.getSnapshot().transcript
        return
      }
    }

    transcriptRef.current = params.transcript
    clientSessionViewStore.replaceOptimisticTranscript(params.transcript)
  }, [])

  const persistMessageToServer = useCallback(
    async (
      targetSessionId: string,
      _role: 'user' | 'model',
      _content: string,
      _timestamp: Date,
      _attachment?: { mimeType: string; data: string },
      _source?: 'text_chat' | 'voice_transcript'
    ) => {
      if (_source === 'voice_transcript') return
      await hydrateSessionProjection(targetSessionId, undefined, { force: true })
    },
    [hydrateSessionProjection]
  )

  return {
    transcript,
    setTranscript,
    transcriptRef,
    backendStatus,
    setBackendStatus,
    applyServerTurnResult,
    persistMessageToServer,
    hydrateSessionProjection,
  }
}
